import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Image, Spinner, Alert, Modal } from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import { AuthService } from '../Services/AuthService';
import { ApiService } from '../Services/APIService';
import { StorageUtils } from '../Utils/StorageUtils';
import { useUserProfile } from '../contexts/UserProfileContext';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { refreshProfile, profileData } = useUserProfile();
  const [loading, setLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    image: '/assets/default_user.png'
  });
  
  const [errors, setErrors] = useState({
    name: '',
    email: ''
  });
  
  
  const fileInputRef = useRef();
  const [previewImage, setPreviewImage] = useState('/assets/default_user.png');
  const [selectedImage, setSelectedImage] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Load user data from cached profile context - updates reactively when profile changes
  useEffect(() => {
    if (profileData && profileData.user) {
      const user = profileData.user;
      console.log('⚡ SettingsPage: Loading from cached profile context:', user);
      
      // Get image with cache busting
      const profileImageUrl = user.profile_image;
      let imageUrl = '/assets/default_user.png';
      if (profileImageUrl && profileImageUrl !== '/assets/default_user.png') {
        const separator = profileImageUrl.includes('?') ? '&' : '?';
        imageUrl = `${profileImageUrl}${separator}t=${Date.now()}`;
      }
      
      setUserInfo({
        name: user.name || '',
        email: user.email || '',
        image: imageUrl
      });
      setPreviewImage(imageUrl);
      setErrors({ name: '', email: '' });
    } else {
      console.log('⚠️ SettingsPage: No profile data available in context yet');
    }
  }, [profileData]); // Re-run when profileData changes
  
  const handleInputChange = (field, value) => {
    setUserInfo(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => ({
      ...prev,
      [field]: '' // clear error while typing
    }));
  };
  
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
        setUserInfo(prev => ({
          ...prev,
          image: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };
  
  const validate = () => {
    let valid = true;
    let newErrors = {};
    
    if (!userInfo.name.trim()) {
      newErrors.name = 'Name is required.';
      valid = false;
    } else if (userInfo.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters.';
      valid = false;
    }
    
    // Email is read-only, so no validation needed
    
    setErrors(newErrors);
    return valid;
  };
  
  const handleSave = async () => {
    if (validate()) {
      try {
        setLoading(true);
        const formData = new FormData();
        formData.append('name', userInfo.name);
        formData.append('email', userInfo.email);
        
        // Always send profile image - either new one or current one as blob
        if (selectedImage) {
          console.log('🖼️ Settings: Sending NEW image with FormData');
          formData.append('profile_image', selectedImage);
        } else if (previewImage && previewImage !== '/assets/default_user.png') {
          console.log('📝 Settings: Sending CURRENT image as blob to preserve it');
          // Convert current image URL to blob and send it
          try {
            const response = await fetch(previewImage);
            const blob = await response.blob();
            formData.append('profile_image', blob, 'current_profile_image.jpg');
          } catch (error) {
            console.log('⚠️ Settings: Could not fetch current image, proceeding without it');
          }
        } else {
          console.log('📝 Settings: No image to preserve (using default)');
        }
        
        // Log FormData contents for debugging
        console.log('📤 Settings FormData contents:', {
          name: userInfo.name,
          email: userInfo.email,
          hasNewImage: !!selectedImage,
          currentPreviewImage: previewImage
        });
        
        const response = await AuthService.updateProfile(formData);
        console.log('📥 Settings API Response:', response.data);
        
        const token = StorageUtils.getUserData()?.token;
        const data = {
          token: token,
          user: response.data.user,
        };
        
        StorageUtils.saveUserData(data);
        setApiResponse({ success: 'Profile updated successfully!' });
        
        // Reset selectedImage after successful save to prevent re-sending old image
        setSelectedImage(null);
        
        // Update local state with new user data
        const profileImageUrl = response.data.user.profile_image;
        let imageUrl = '/assets/default_user.png';
        
        // Add cache-busting parameter to profile image URL
        if (profileImageUrl && profileImageUrl !== '/assets/default_user.png') {
          const separator = profileImageUrl.includes('?') ? '&' : '?';
          imageUrl = `${profileImageUrl}${separator}t=${Date.now()}`;
        }
        
        setUserInfo({
          name: response.data.user.name || '',
          email: response.data.user.email || '',
          image: imageUrl
        });
        setPreviewImage(imageUrl);
        
        // Refresh profile data in context (will update all components using the profile)
        await refreshProfile();
        
        window.dispatchEvent(new Event('storage'));
        
        setTimeout(() => {
          setApiResponse(null);
        }, 3000);
      } catch (error) {
        console.error('Error saving profile:', error);
        setApiResponse({
          error: error.response?.data?.message || 'Failed to save profile'
        });
        setTimeout(() => {
          setApiResponse(null);
        }, 3000);
      } finally {
        setLoading(false);
      }
    }
  };
  
  
  
  const handleAvatarEdit = () => {
    fileInputRef.current.click();
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const token = StorageUtils.getUserData()?.token;
      
      ApiService.setAuthToken(token);
      const response = await AuthService.deleteAccount();
      console.log('📥 Settings API Response:', response.data);

      StorageUtils.clearAllUserCache(); // Clear all user cache
      window.dispatchEvent(new Event('storage'));
      navigate('/auth?type=login'); // Redirect to login screen
      setIsDeleting(false);

    } catch (err) {
      console.error('Delete failed', err);
    } finally {
      setIsDeleting(false);
      setShowModal(false);
    }
  };

  const handleLogout = () => {
    console.log('🚪 Logging out user...');
    
    // Clear all user-related cache
    StorageUtils.clearAllUserCache();
    
    // Notify components that user data changed
    window.dispatchEvent(new Event('storage'));
    
    // Small delay to ensure cache is cleared before navigation
    setTimeout(() => {
      navigate('/auth?type=login');
    }, 100);
  };


  return (
    <Container fluid className="settings-container" style={{ padding: '2rem' }}>
      <Row>
        <Col>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <h2 style={{ margin: 0, fontWeight: '600' }}>Settings</h2>
            <Button
              variant="primary"
              onClick={handleLogout}
              style={{ fontWeight: '700', borderRadius: '8px' }}
            >
              Log out
            </Button>
          </div>
          
          {/* Personal Section */}
          <Card style={{ marginBottom: '2rem', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            <Card.Body style={{ padding: '2rem' }}>
              <h5 style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '600', marginBottom: '2rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                PERSONAL
              </h5>
              
              {/* Name Field */}
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ fontSize: '1rem', fontWeight: '500', color: '#333' }}>Name</label>
                </div>
                <Form.Control
                  type="text"
                  value={userInfo.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  style={{ 
                    border: 'none', 
                    borderBottom: '1px solid #e9ecef', 
                    borderRadius: '0',
                    padding: '0.5rem 0',
                    fontSize: '1rem',
                    backgroundColor: 'transparent'
                  }}
                />
                {errors.name && (
                  <div className="text-danger small mt-1">
                    {errors.name}
                  </div>
                )}
              </div>

              {/* Email Field */}
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ fontSize: '1rem', fontWeight: '500', color: '#333' }}>Email</label>
                </div>
                <Form.Control
                  type="email"
                  value={userInfo.email}
                  readOnly
                  style={{ 
                    border: 'none', 
                    borderBottom: '1px solid #e9ecef', 
                    borderRadius: '0',
                    padding: '0.5rem 0',
                    fontSize: '1rem',
                    backgroundColor: 'transparent',
                    cursor: 'not-allowed',
                    color: '#6c757d'
                  }}
                />
                {errors.email && (
                  <div className="text-danger small mt-1">
                    {errors.email}
                  </div>
                )}
              </div>

              {/* Avatar Field */}
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <label style={{ fontSize: '1rem', fontWeight: '500', color: '#333' }}>Avatar</label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                  <div 
                    onClick={handleAvatarEdit}
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                  >
                    <Image
                      src={previewImage}
                      alt="Profile"
                      width={60}
                      height={60}
                      style={{ objectFit: 'cover' }}
                    />
                  </div>
                  {/* Hidden file input */}
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>

              {/* Save Changes Button */}
              <div style={{ marginBottom: '2rem' }}>
                <Button 
                  variant="primary" 
                  onClick={handleSave}
                  disabled={loading}
                  style={{
                    padding: '0.75rem 2rem',
                    fontWeight: '500',
                    borderRadius: '8px'
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                      {' Saving...'}
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>

              {/* API Response Alert */}
              {apiResponse && (
                <Alert variant={apiResponse.error ? 'danger' : 'success'} className="mb-3">
                  {apiResponse.error || apiResponse.success}
                </Alert>
              )}

            </Card.Body>
          </Card>

          {/* Delete Account Section */}
          <Card style={{ border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            <Card.Body style={{ padding: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h5 style={{ fontSize: '1rem', fontWeight: '500', color: '#333', marginBottom: '0.25rem' }}>Delete Account</h5>
                </div>
                <Button 
                  variant="primary" 
                  style={{ padding: '6px 12px', fontSize: '0.9rem', fontWeight: '700', textDecoration: 'none' }}
                  onClick={() => setShowModal(true)}
                >
                  Delete account
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Modal
            show={showModal}
            onHide={() => setShowModal(false)}
            centered
            backdrop="static"
          >
            <Modal.Header closeButton>
              <Modal.Title>Delete Account?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to delete your account? This action is irreversible.
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
                style={{ fontWeight: '700' }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDelete}
                disabled={isDeleting}
                style={{ fontWeight: '700' }}
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </Button>
            </Modal.Footer>
          </Modal>

        </Col>
      </Row>
    </Container>
  );
};

export default SettingsPage;